import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Lock, Phone } from 'lucide-react'
import { toast } from 'sonner'

export default function Login() {
  const [phone, setPhone] = useState('+91')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [language, setLanguage] = useState<'en' | 'hi'>('en')
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await login(phone, password)
      // Show success message
      toast.success('Login successful')
      // Redirect to dashboard - RoleBasedRouter will handle user-specific routing
      console.log('Login successful, redirecting to dashboard')
      navigate('/dashboard')
    } catch (error) {
      console.error('Login failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Remove all non-digit characters except +
    const cleaned = value.replace(/[^\d+]/g, '')

    // Always keep +91 prefix
    if (cleaned.startsWith('+91')) {
      setPhone(cleaned)
    } else if (cleaned.startsWith('+9')) {
      setPhone('+91')
    } else if (cleaned.startsWith('+')) {
      setPhone('+91')
    } else {
      // If user types only digits, prepend +91
      const digitsOnly = cleaned.replace(/\+/g, '')
      setPhone('+91' + digitsOnly)
    }
    setError('')
  }

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')

    // Clean the pasted text
    const cleaned = pastedText.replace(/[^\d+]/g, '')

    // If it starts with +91, use as is
    if (cleaned.startsWith('+91')) {
      setPhone(cleaned.slice(0, 13)) // Limit to max length
    } else if (cleaned.startsWith('91') && cleaned.length >= 10) {
      // If starts with 91 (without +), add the +
      setPhone('+' + cleaned.slice(0, 12))
    } else if (cleaned.match(/^\d{10}$/)) {
      // If it's exactly 10 digits, prepend +91
      setPhone('+91' + cleaned)
    } else {
      // For any other format, try to extract digits and prepend +91
      const digitsOnly = cleaned.replace(/\+/g, '')
      if (digitsOnly.length >= 10) {
        setPhone('+91' + digitsOnly.slice(-10))
      } else {
        setPhone('+91' + digitsOnly)
      }
    }
    setError('')
  }

  const handlePasswordPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    setPassword(pastedText)
    setError('')
  }


  const translations = {
    en: {
      title: 'SAUBHAGYA',
      subtitle: 'Biogas Management & Rural Energy Distribution',
      phone: 'Phone Number',
      password: 'Password',
      login: 'Login',
      loading: 'Logging in...',
      phonePlaceholder: '+91 (10-digit mobile number)',
      passwordPlaceholder: 'Enter your password',
      invalidCredentials: 'Invalid phone number or password. Please try again.',
      showPassword: 'Show password',
      hidePassword: 'Hide password'
    },
    hi: {
      title: 'सौभाग्य',
      subtitle: 'बायोगैस प्रबंधन और ग्रामीण ऊर्जा वितरण',
      phone: 'फ़ोन नंबर',
      password: 'पासवर्ड',
      login: 'लॉगिन',
      loading: 'लॉगिन हो रहा है...',
      phonePlaceholder: '+91 (10 अंकों का मोबाइल नंबर)',
      passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
      invalidCredentials: 'अमान्य फ़ोन नंबर या पासवर्ड। कृपया पुनः प्रयास करें।',
      showPassword: 'पासवर्ड दिखाएं',
      hidePassword: 'पासवर्ड छुपाएं'
    }
  }

  const t = translations[language]

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4">
      {/* Background Video/Image */}
      <div className="absolute inset-0 z-0">
        {/* Wide panoramic pastoral landscape with cows */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-105"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2560&q=80"), url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2560&q=80"), url("https://images.unsplash.com/photo-1595058542840-20d2ce4e77f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2560&q=80"), linear-gradient(135deg, #22c55e 0%, #059669 100%)`
          }}
        ></div>

        {/* Balanced natural gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/75 via-blue-900/70 to-emerald-900/75 z-10"></div>

        {/* Additional texture overlay */}
        <div
          className="absolute inset-0 opacity-20 z-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>

        {/* Subtle natural floating animations */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Soft natural orbs */}
          <div className="absolute top-1/4 left-1/6 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/5 w-48 h-48 bg-slate-400/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-2/3 left-1/2 w-24 h-24 bg-teal-400/12 rounded-full blur-xl animate-pulse" style={{animationDelay: '4s'}}></div>
          <div className="absolute top-1/6 right-1/3 w-40 h-40 bg-cyan-400/8 rounded-full blur-2xl animate-pulse" style={{animationDelay: '6s'}}></div>

          {/* Floating particles */}
          <div className="absolute top-1/5 left-3/4 w-2 h-2 bg-white/30 rounded-full animate-bounce" style={{animationDelay: '1s', animationDuration: '3s'}}></div>
          <div className="absolute bottom-1/4 left-1/5 w-1 h-1 bg-blue-300/40 rounded-full animate-bounce" style={{animationDelay: '3s', animationDuration: '4s'}}></div>
          <div className="absolute top-1/2 right-1/6 w-1.5 h-1.5 bg-teal-300/35 rounded-full animate-bounce" style={{animationDelay: '5s', animationDuration: '3.5s'}}></div>
        </div>

        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-20 animate-in fade-in slide-in-from-bottom-4 duration-1000">
        {/* Language Toggle */}
        <div className="flex justify-end">
          <button
            onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
            className="px-4 py-2 text-sm bg-white/10 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 hover:bg-white/20 text-white transition-all duration-300"
          >
            {language === 'en' ? 'हिंदी' : 'English'}
          </button>
        </div>

        {/* Header */}
        <div className="text-center">
          {/* Logo/Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-28 h-28 bg-white/95 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-110 transition-all duration-300 border border-white/30 p-1">
                {/* Your Original Dhanush Logo */}
                <img
                  src="/images/dhanush_group_logo.jpg"
                  alt="Dhanush Group Logo"
                  className="w-full h-full object-contain rounded-xl"
                />
              </div>
              {/* Animated ring */}
              <div className="absolute inset-0 w-28 h-28 border-2 border-white/20 rounded-2xl animate-ping"></div>
              <div className="absolute inset-2 w-24 h-24 border border-white/10 rounded-xl animate-pulse"></div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">
            {t.title}
          </h1>
          <p className="text-white/90 text-lg mb-8 drop-shadow">
            {t.subtitle}
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-white/20 space-y-6">
          {/* Phone Number Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
              {t.phone}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70 h-5 w-5" />
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                onPaste={handlePhonePaste}
                onKeyDown={(e) => {
                  // Allow: Ctrl+V, Ctrl+C, Ctrl+X, Ctrl+A for copy-paste operations
                  if (e.ctrlKey || e.metaKey) {
                    return
                  }

                  // Allow: backspace, delete, tab, escape, enter, +, arrows
                  if (
                    e.key === 'Backspace' ||
                    e.key === 'Delete' ||
                    e.key === 'Tab' ||
                    e.key === 'Escape' ||
                    e.key === 'Enter' ||
                    e.key === '+' ||
                    e.key === 'ArrowLeft' ||
                    e.key === 'ArrowRight' ||
                    e.key === 'Home' ||
                    e.key === 'End' ||
                    e.key === 'ArrowDown' ||
                    e.key === 'ArrowUp'
                  ) {
                    return
                  }
                  // Block if not a number
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault()
                  }
                }}
                placeholder={t.phonePlaceholder}
                maxLength={13}
                className="w-full pl-10 pr-3 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 text-white placeholder-white/70 transition-all duration-300"
                required
                autoComplete="tel"
              />
            </div>
          </div>

          {/* Password Field with Tooltip */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
              {t.password}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70 h-5 w-5" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError('')
                }}
                onPaste={handlePasswordPaste}
                placeholder={t.passwordPlaceholder}
                className="w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 text-white placeholder-white/70 transition-all duration-300"
                required
                autoComplete="current-password"
              />
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseEnter={() => setShowPasswordTooltip(true)}
                  onMouseLeave={() => setShowPasswordTooltip(false)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors duration-200"
                  aria-label={showPassword ? t.hidePassword : t.showPassword}
                  style={{ top: '-18px' }}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>

                {/* Tooltip for password visibility toggle */}
                {showPasswordTooltip && (
                  <div
                    className="absolute right-0 bottom-full mb-2 px-3 py-1.5 bg-gray-900/95 text-white text-xs rounded-md shadow-lg whitespace-nowrap animate-in fade-in slide-in-from-bottom-1 duration-200 pointer-events-none"
                    style={{ top: '-50px', right: '0px' }}
                  >
                    {showPassword ? t.hidePassword : t.showPassword}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-4 border-transparent border-t-gray-900/95"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-red-100 text-sm text-center font-medium">
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-green-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
          >
            {loading ? t.loading : t.login}
          </button>
        </form>
      </div>
    </div>
  )
}
